/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { format as formatNotification, getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { Address, AddressPurpose, CountryRegionInfo, StateProvinceInfo, IContext } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import { action, observable, reaction, set } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { AutoSuggest } from './common/address-autosuggest';
import { IAutoSuggestOptions } from './common/address-autosuggest.data';
import { vSIInitiateRestCallAsync } from '../../dotdigital-extension/DataActionExtension.g';
import { IDeliveryOption, IPostCodeDropDownOption, IPostCodeVerification } from '../dobbies-checkout-shipping-address';
import { AddressCommon } from './common/address-common';
import { AddressFormat } from './common/address-format';
import { AddressItemType } from './common/address-format.data';
import { AddressMetaData } from './common/address-meta-data';
import { AddressOperation, AddressType, IAddressResource, IAddressResponse } from './common/address-module.data';
import { AddressAddUpdate, IAddressAddUpdateProps } from './common/components/address-add';
import { AddressList, IAddressListProps } from './common/components/address-list';
import { IDobbiesAccountManagementAddressData } from './dobbies-account-management-address.data';
import { IDobbiesAccountManagementAddressProps } from './dobbies-account-management-address.props.autogenerated';

export interface IAccountManagementAddressViewState {
    isShowAddresList: boolean;
    isShowAddOrUpdateAddress: boolean;
}

export interface IDobbiesAccountManagementAddressState {
    postCodeEmptyErrorMessage: string;
    addressOptions: IPostCodeDropDownOption[];
    postalOptions: IPostCodeDropDownOption[];
}

export interface IAccountManagementAddressViewProps
    extends React.Component<IDobbiesAccountManagementAddressProps<IDobbiesAccountManagementAddressData>> {
    className: string;
    currentOperation: AddressOperation;
    selectedAddress?: Address;
    addUpdateAddress: Address;
    countryRegionId: string;
    stateProvinceInfo?: StateProvinceInfo[];
    customerAddresses: Address[];
    validationError: object;
    addressActionResponse?: IAddressResponse;
    viewState: IAccountManagementAddressViewState;
    AccountAddressManagement: IModuleProps;
    isUpdating: boolean;
    showAddressList: IAddressListProps;
    showAddOrUpdateAddress: IAddressAddUpdateProps;
    renderInputWithButton: React.ReactNode;
    screenReaderNotification: React.ReactNode;
}

/**
 *
 * Address component
 * @extends {React.Component<IDobbiesAccountManagementAddressProps<IDobbiesAccountManagementAddressData>>}
 */
@observer
class AccountManagementAddress extends React.Component<
    IDobbiesAccountManagementAddressProps<IDobbiesAccountManagementAddressData>,
    IDobbiesAccountManagementAddressState
> {
    @observable private currentOperation: AddressOperation;
    @observable private selectedAddress?: Address;
    @observable private countryRegionId: string = 'USA';
    @observable private stateProvinceInfo?: StateProvinceInfo[];
    @observable private customerAddresses: Address[];
    @observable private addUpdateAddress: Address;
    @observable private validationError: object;
    @observable private addressActionResponse?: IAddressResponse;
    @observable private isUpdating?: boolean;
    @observable private hasError?: boolean;
    @observable private removeAddressData?: Address;

    private addressCommon: AddressCommon;
    private addressFormat: AddressFormat;
    private countryRegions: CountryRegionInfo[];
    private addressPurposes: AddressPurpose[];
    private resources: IAddressResource;
    private defaultAddressType: number = 6; // Default to Home
    private telemetryContent: ITelemetryContent;
    private autoSuggest?: AutoSuggest;
    /* VSI-Customization -- START Post code look related  */
    private postCodeInputRef: React.RefObject<HTMLInputElement>;
    /* VSI-Customization -- END  */
    constructor(props: IDobbiesAccountManagementAddressProps<IDobbiesAccountManagementAddressData>) {
        super(props);
        const { context, data, resources, telemetry } = this.props;

        this.resources = resources;
        this.currentOperation = AddressOperation.List;
        this.countryRegions = data.countryRegions.result || [];
        this.addressPurposes = data.addressPurposes.result || [];
        this.customerAddresses = data.address.result || [];
        this.addUpdateAddress = {};
        this.validationError = {};
        this.addressCommon = new AddressCommon(context, resources, telemetry);
        this.addressFormat = new AddressFormat(
            this.countryRegions,
            new AddressMetaData({ ...resources }, this._getAddressFormatExcludeList()),
            this.addressPurposes
        );
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        /* VSI-Customization -- START post code lookup related  */
        this.postCodeInputRef = React.createRef<HTMLInputElement>();
        /* VSI-Customization -- END  */
        this.state = {
            addressOptions: [],
            postalOptions: [],
            postCodeEmptyErrorMessage: ''
        };
    }

    public async componentDidMount(): Promise<void> {
        const {
            context: {
                telemetry,
                actionContext: {
                    requestContext: { channel }
                }
            },
            config: { autoSuggestionEnabled, autoSuggestOptions }
        } = this.props;

        // Initializing data props
        this._dataInitialize(this.props);

        this.props.data.countryRegions.then(() => {
            this._setDefaultCountryRegionId();
        });
        reaction(
            () => this.countryRegionId,
            () => this._getStateProvinces()
        );
        reaction(
            () => this.currentOperation,
            () => {
                this._getStateProvinces();
            }
        );
        if (autoSuggestionEnabled) {
            if (channel && !channel.BingMapsApiKey) {
                telemetry.error('BingMapsApiKey is missing.');
                return;
            }
            if (channel && !channel.BingMapsEnabled) {
                telemetry.error('Map is disabled from HQ.');
                return;
            }
            const options: IAutoSuggestOptions = { ...autoSuggestOptions };
            this.autoSuggest = new AutoSuggest(
                telemetry,
                options,
                channel?.BingMapsApiKey,
                channel?.ChannelCountryRegionISOCode,
                channel?.DefaultLanguageId
            );
            reaction(
                () =>
                    this.props.data.storeSelectorStateManager.result?.loadMapApi !== undefined &&
                    (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update),
                async () => {
                    await this.autoSuggest?._loadMapAPI(await this.props.data.storeSelectorStateManager);
                }
            );
            reaction(
                () => {
                    this.props.data.storeSelectorStateManager.result?.isMapApiLoaded;
                },
                async () => {
                    if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
                        await this._attachMapAutoSuggest();
                    }
                }
            );
        }
    }

    public async componentDidUpdate(): Promise<void> {
        if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
            if (this.props.data.storeSelectorStateManager.result?.isMapApiLoaded) {
                await this._attachMapAutoSuggest();
            }
        } else {
            this.autoSuggest?.disposeAutoSuggest();
        }
    }

    /**
     * Should component update function.
     * @param nextProps - Account management address props.
     * @param nextState - Next state.
     * @returns - True if component should update, false otherwise.
     */

    public shouldComponentUpdate(
        nextProps: IDobbiesAccountManagementAddressProps<IDobbiesAccountManagementAddressData>,
        nextState: IDobbiesAccountManagementAddressState
    ): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { renderView, config } = this.props;

        const viewProps = {
            ...this.props,
            renderInputWithButton: this._renderInputWithButton(),
            currentOperation: this.currentOperation,
            selectedAddress: this.selectedAddress,
            addUpdateAddress: this.addUpdateAddress,
            countryRegionId: this.countryRegionId,
            stateProvinceInfo: this.stateProvinceInfo,
            customerAddresses: this.customerAddresses,
            validationError: this.validationError,
            addressActionResponse: this.addressActionResponse,
            isUpdating: this.isUpdating,
            hasError: this.hasError,
            className: config.className,
            viewState: {
                isShowAddresList: this.currentOperation === AddressOperation.List,
                isShowAddOrUpdateAddress: this.currentOperation !== AddressOperation.List
            },
            AccountAddressManagement: {
                moduleProps: this.props,
                className: classnames('ms-account-management-address', config.className)
            },
            showAddressList: this._renderListAddress(),
            showAddOrUpdateAddress: this._renderAddOrUpdateAddress(),
            screenReaderNotification: this.removeAddressData ? this._renderScreenReaderRemoveText(this.removeAddressData) : null
        };

        return renderView(viewProps) as React.ReactElement;
    }

    /**
     * Method data initialization.
     * @param props -The account management props.
     */

    private readonly _dataInitialize = (props: IDobbiesAccountManagementAddressProps<IDobbiesAccountManagementAddressData>): void => {
        const { data } = props;
        reaction(
            () => data.countryRegions.result,
            () => {
                this.countryRegions = data.countryRegions.result ?? [];
            }
        );
        reaction(
            () => data.addressPurposes.result,
            () => {
                this.addressPurposes = data.addressPurposes.result ?? [];
            }
        );
        reaction(
            () => data.address.result,
            () => {
                this.customerAddresses = data.address.result ?? [];
            }
        );
    };

    private readonly _renderAddOrUpdateAddress = (): IAddressAddUpdateProps => {
        return AddressAddUpdate({
            isUpdating: this.isUpdating,
            resources: this.resources,
            addressType: AddressType.Shipping,
            addressFormat: this.addressFormat.getAddressFormat(this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId),
            defaultCountryRegionId: this.countryRegionId,
            defaultAddressType: this.defaultAddressType,
            selectedAddress: this.addUpdateAddress,
            validationError: this.validationError,
            addressActionResponse: this.addressActionResponse,
            postalOptions: this.state.postalOptions || [],
            dropdownDisplayData: this.addressFormat.getPrefilledAddressDropdownData(
                this.resources.addressStateDefaultSelectionText,
                this.stateProvinceInfo
            ),
            telemetryContent: this.telemetryContent,
            onInputChange: this._onAddressAddUpdateInputChange,
            onDropdownChange: this._onAddressAddUpdateDropdownChange,
            onSave: this._onAddressAddUpdateSubmit,
            onCancel: this._resetView
        });
    };

    private findAddress = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.currentTarget;

        if (value && value.length >= 5) {
            const context: IContext = { callerContext: this.props.context.actionContext };
            const urlParams = {
                key: '1024c-01f0c-70dd6-06464',
                postcode: this.postCodeInputRef.current?.value,
                response: 'data_formatted'
            };
            const res = await vSIInitiateRestCallAsync(
                context,
                'Fetchify',
                'get',
                '{"cache-control":"no-cache", "Accept":"application/json" }',
                '',
                JSON.stringify(urlParams),
                `/json/rapidaddress`
            );
            if (res.result) {
                const responseResult: IPostCodeVerification = JSON.parse(res.result);
                /* VSI-Customization -- START If response got from API map it into array and update it with state (setState)  */
                if (!responseResult.error_msg) {
                    const addressOptions = this.mapPostCodeOptions(responseResult);
                    this.setState({ addressOptions: addressOptions, postCodeEmptyErrorMessage: '' });
                    /* VSI-Customization -- START If error code returns it means postcode isn't valid  */
                } else {
                    this.setState({ addressOptions: [], postCodeEmptyErrorMessage: this.props.resources.validPostCodeErrorText });
                }
            }
        } else if (value.length < 5) {
            this.setState({ addressOptions: [], postCodeEmptyErrorMessage: '' });
        }
    };
    /* VSI-Customization -- END  */

    private findPostalAddress = async (value: string) => {
        if (value && value.length >= 5) {
            const context: IContext = { callerContext: this.props.context.actionContext };
            const urlParams = {
                key: '1024c-01f0c-70dd6-06464',
                postcode: value,
                response: 'data_formatted',
                sort: 'asc'
            };

            const res = await vSIInitiateRestCallAsync(
                context,
                'Fetchify',
                'get',
                "{'cache-control':'no-cache' , 'Accept':'application/json'}",
                '',
                JSON.stringify(urlParams),
                `/json/rapidaddress`
            );
            if (res.result) {
                const responseResult: IPostCodeVerification = JSON.parse(res.result);
                /* VSI-Customization -- START If response got from API map it into array and update it with state (setState)  */
                if (!responseResult.error_msg) {
                    const postalOptions = this.mapPostCodeOptions(responseResult);
                    this.setState({ postalOptions: postalOptions, postCodeEmptyErrorMessage: '' });
                    /* VSI-Customization -- START If error code returns it means postcode isn't valid  */
                } else {
                    this.setState({ postalOptions: [] });
                }
            }
        } else if (value.length < 5) {
            this.setState({ postalOptions: [], postCodeEmptyErrorMessage: '' });
        }
    };

    /* VSI-Customization -- START If postcode is invalid display the error text message that please enter a valid postcode  */
    private _renderEmptyPostCodeErrorMessage = () => {
        return (
            <>
                {this.state.postCodeEmptyErrorMessage && (
                    <div className='post-code'>
                        <p className='post-code-error'>{this.state.postCodeEmptyErrorMessage}</p>
                    </div>
                )}
            </>
        );
    };
    /* VSI-Customization -- END  */

    /* VSI-Customization -- START When user selects option from autosuggestion box embed the selected option values into form fields  */
    private handleAddressOption = (option: IPostCodeDropDownOption) => (e: React.MouseEvent<HTMLDivElement>) => {
        const { addressOptions } = this.state;
        if (addressOptions && addressOptions.length > 0) {
            const addressOption = addressOptions.find(x => x.option.toLowerCase() === option.option.toLowerCase());
            if (addressOption) {
                // populate values into form
                this.onAddressAddUpdate('City', addressOption.town);
                this.onAddressAddUpdate('Street', `${addressOption.postCode.line_1}, ${addressOption.postCode.line_2}`);
                this.onAddressAddUpdate('ZipCode', addressOption.code);
                // clear the postcode input field
                // clear the array
                this.postCodeInputRef.current!.value = '';
                this.setState({ addressOptions: [] });
            } else {
                this.onAddressAddUpdate('City', '');
                this.onAddressAddUpdate('Street', '');
                this.onAddressAddUpdate('ZipCode', '');
            }
        }
    };
    /* VSI-Customization -- END  */

    /* VSI-Customization -- START Created JSX based on options retrieved from API  */
    private _renderAddressOptions = (): React.ReactNode => {
        const { addressOptions } = this.state;
        if (addressOptions && addressOptions.length > 0) {
            return (
                <div>
                    {addressOptions
                        .sort((a, b) => a.option.localeCompare(b.option))
                        .map((option: IPostCodeDropDownOption) => {
                            return (
                                // tslint:disable-next-line: react-a11y-event-has-role
                                <div key={option.dps} onClick={this.handleAddressOption(option)}>
                                    {option.option}
                                </div>
                            );
                        })}
                </div>
            );
        }
        return;
    };
    /* VSI-Customization -- END  */

    /* VSI-Customization -- START Render input field with button  */
    private _renderInputWithButton = () => {
        return (
            <div className='postcode-validation'>
                <input
                    type='text'
                    className='msc-address-form__input'
                    onChange={this.findAddress}
                    ref={this.postCodeInputRef}
                    name='postcode'
                    id='fetchify-postcode'
                    placeholder={this.props.resources.enterPostCodePlaceholderText}
                />
                {this.state.addressOptions?.length > 0 ? (
                    <div className='auto-suggestion'>{this._renderAddressOptions()}</div>
                ) : (
                    <div className='post-code-errorbox'>{this._renderEmptyPostCodeErrorMessage()}</div>
                )}
            </div>
        );
    };
    /* VSI-Customization -- END  */

    /* VSI-Customization -- START Map response data into array and update state with mapped array  */
    private mapPostCodeOptions = (data: IPostCodeVerification) => {
        const addressOptions: IPostCodeDropDownOption[] = [];
        data &&
            data.delivery_points.map((option: IDeliveryOption) => {
                // create object and push it into array of type IPostCodeDropDownOption
                const obj: IPostCodeDropDownOption = {
                    option: `${option.line_1}, ${option.line_2}, ${data.town}`,
                    dps: option.dps,
                    postCode: option,
                    town: data.town,
                    code: data.postcode
                };
                addressOptions.push(obj);
            });
        return addressOptions;
    };
    /* VSI-Customization -- END  */

    private _renderListAddress = (): IAddressListProps => {
        const { config } = this.props;
        /**
         * Render Address ttitle.
         * @param event - Msdyn365.ContentEditableEvent.
         */

        const handleLineItemHeadingChange = (event: Msdyn365.ContentEditableEvent) => {
            config.heading!.text = event.target.value;
        };
        const heading = config.heading && {
            text: config.heading.text,
            headingTag: config.heading.tag
        };

        return AddressList({
            isUpdating: this.isUpdating,
            selectedAddress: this.selectedAddress,
            addressFormat: this.addressFormat,
            addresses: this.customerAddresses,
            addressPurposes: this.addressPurposes,
            resources: this.resources,
            heading,
            primaryAddressSectionHeading: config.primaryAddressSectionHeading,
            otherAddressSectionHeading: config.otherAddressSectionHeading,
            contextRequest: this.props.context.request,
            telemetryContent: this.telemetryContent,
            handleLineItemHeadingChange,
            onEditAddress: this._goToEditAddress,
            onRemoveAddress: this._onRemoveAddress,
            onAddAddress: this._goToAddAddress,
            onUpdatePrimaryAddress: this._onSubmitUpdatePrimaryAddress
        });
    };
    private _attachMapAutoSuggest = async (): Promise<void> => {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;
        if (storeSelectorStateManager?.isMapApiLoaded) {
            this.autoSuggest?.attachAutoSuggest('#shipping_addressstreet', '#shipping_addressstreet_container', this._onSuggestionSelected);
        }
    };
    @action
    private _onSuggestionSelected = async (result: Microsoft.Maps.ISuggestionResult): Promise<void> => {
        this._clearAddressFields();
        const address = this.addressFormat.getTranformedAddress(result, this.stateProvinceInfo);
        set(this.addUpdateAddress, { Street: '' });
        set(this.addUpdateAddress, { ZipCode: address.ZipCode });
        set(this.addUpdateAddress, { CountyName: address.CountyName });
        set(this.addUpdateAddress, { City: address.City });
        set(this.addUpdateAddress, { State: address.State });
        set(this.addUpdateAddress, { DistrictName: address.DistrictName });
        set(this.addUpdateAddress, { FullAddress: address.FullAddress });
        // Bing autosuggest put the complete address in the Street input box. Updating the street input box to show only street address.
        setTimeout(() => {
            set(this.addUpdateAddress, { ['Street']: address.Street });
        }, 0);
    };
    @action
    private _clearAddressFields = (): void => {
        const addressFormatItem = this.addressFormat.getAddressFormat(
            this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId
        );
        addressFormatItem.forEach(format => {
            if (this.addUpdateAddress[format.name] !== undefined && !this.autoSuggest?.excludedAddressFields.includes(format.name)) {
                this.addressFormat[format.name] = '';
            }
        });
        this._clearValidation();
    };
    @action
    private _clearValidation = (): void => {
        this.validationError = {};
    };
    private onAddressAddUpdate = (name: string, value: string | boolean | number) => {
        if (name === 'ZipCode') {
            this.findPostalAddress(value as string);
        }
        set(this.addUpdateAddress, { [name]: value });
        this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId, name);
    };

    private _onAddressAddUpdateInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.type === 'checkbox') {
            this.onAddressAddUpdate(event.target.name, event.target.checked);
        } else {
            const value = (event.target.value || '').replace(new RegExp('[<>]', 'gi'), '');
            this.onAddressAddUpdate(event.target.name, value);
        }
    };

    private _onAddressAddUpdateDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        if (event.target.name === AddressItemType[AddressItemType.AddressTypeValue]) {
            this.onAddressAddUpdate(event.target.name, +event.target.value);
        } else {
            this.onAddressAddUpdate(event.target.name, event.target.value);
        }
        if (event.target.name === AddressItemType[AddressItemType.ThreeLetterISORegionName]) {
            this._onCountryChange(event.target.value);
        }
    };

    private _onAddressUpdate = (response: Promise<IAddressResponse>, onSuccess: (response: IAddressResponse) => void): void => {
        this.isUpdating = true;
        response.then(
            (result: IAddressResponse) => {
                if (result.address) {
                    this.hasError = false;
                    onSuccess(result);
                } else {
                    this.hasError = true;
                    this.addressActionResponse = result;
                }
                this.isUpdating = false;
            },
            () => {
                this.hasError = true;
                this.isUpdating = false;
            }
        );
    };

    private _onAddressAddUpdateSubmit = (): void => {
        if (!this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId)) {
            return;
        }

        let response: Promise<IAddressResponse>;
        if (this.addressCommon.isAuthenticatedFlow()) {
            response =
                this.currentOperation === AddressOperation.Update
                    ? this.addressCommon.updateCustomerAddress(this.addUpdateAddress)
                    : this.addressCommon.addCustomerAddress(this.addUpdateAddress);
        } else {
            response = Promise.resolve({ address: this.addUpdateAddress });
        }

        this._onAddressUpdate(response, this._onAddOrUpdateSuccess);
    };

    private _onCountryChange = (countryRegionId: string) => {
        this.countryRegionId = countryRegionId;
        const twoLetterISORegionName = this.addressFormat.getTwoLetterISORegionName(countryRegionId);
        set(this.addUpdateAddress, { ['ThreeLetterISORegionName']: countryRegionId });
        set(this.addUpdateAddress, { TwoLetterISORegionName: twoLetterISORegionName });
        this.autoSuggest?.changeAutoSuggestionCountryCode(twoLetterISORegionName);
        this._clearAddressFields();
    };

    private _getAddressFormatExcludeList = (): AddressItemType[] => {
        const { config } = this.props;
        const addressFormatExcludeList: AddressItemType[] = [];

        if (!config.showAddressType) {
            addressFormatExcludeList.push(AddressItemType.AddressTypeValue);
        }

        addressFormatExcludeList.push(AddressItemType.IsPrimary);

        return addressFormatExcludeList;
    };

    private _getStateProvinces = (): void => {
        if (
            !this.countryRegionId ||
            !(this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update)
        ) {
            return;
        }

        // tslint:disable-next-line:no-floating-promises
        this.addressCommon.getStateProvinces(this.countryRegionId).then((result: StateProvinceInfo[]) => {
            const stateInfo = result.some(state => state.StateId === this.addUpdateAddress.State);
            // Reset state if selected state not found in the list.
            if (!stateInfo) {
                set(this.addUpdateAddress, { State: '' });
            }
            this.stateProvinceInfo = result;
        });
    };

    private _setDefaultCountryRegionId(): void {
        const { request } = this.props.context;
        const market = request && request.channel && request.channel.ChannelCountryRegionISOCode;

        this.countryRegionId = this.addressCommon.getDefaultCountryRegionId(this.countryRegionId, this.countryRegions, market);
    }

    private _updateCurrentOperation = (operation: AddressOperation) => {
        this.currentOperation = operation;
        this._clearAddressFields();
    };

    private _onSubmitUpdatePrimaryAddress = (address: Address): void => {
        this.selectedAddress = address;
        const response = this.addressCommon.updateCustomerPrimaryAddress(address);
        this._onAddressUpdate(response, this._onUpdatePrimaryAddressSuccess);
    };

    private _goToEditAddress = (address: Address): void => {
        this.addUpdateAddress = { ...address };
        this.countryRegionId = this.addUpdateAddress.ThreeLetterISORegionName || 'USA';
        this._updateCurrentOperation(AddressOperation.Update);
    };

    private _onRemoveAddress = (address: Address): void => {
        address.Deactivate = true;
        this.removeAddressData = address;
        const response = this.addressCommon.updateCustomerAddress(address);
        this._onAddressUpdate(response, this._onAddOrUpdateSuccess);
    };

    /**
     * Renders Notification screen reader text for removed address.
     * @param address - Address input.
     * @returns Notification for removed address.
     */

    private readonly _renderScreenReaderRemoveText = (address: Address): React.ReactNode => {
        const notification = formatNotification(this.resources.removeAddressNotification!, `${address.Name!}`);
        return (
            <div className='remove-address-screen-reader' aria-live='polite'>
                {notification}
            </div>
        );
    };

    private _goToAddAddress = (): void => {
        this.addUpdateAddress = {};
        this._setDefaultCountryRegionId();
        set(this.addUpdateAddress, { ThreeLetterISORegionName: this.countryRegionId });
        set(this.addUpdateAddress, { AddressTypeValue: this.defaultAddressType });
        this._setDefaultCountryRegionId();
        this._updateCurrentOperation(AddressOperation.Add);
    };

    private _onAddOrUpdateSuccess = (response: IAddressResponse): void => {
        if (response.customerAddresses) {
            this.customerAddresses = response.customerAddresses;
        }
        this._resetView();
    };

    private _onUpdatePrimaryAddressSuccess = (response: IAddressResponse): void => {
        if (response.customerAddresses) {
            this.customerAddresses = response.customerAddresses;
        }
    };

    private _resetView = (): void => {
        this.selectedAddress = undefined;
        this._updateCurrentOperation(AddressOperation.List);
    };
}

export default AccountManagementAddress;
